import React from 'react';

// Identidad visual de SICREA: navy profundo, latón, crema.
export const T = {
  navy:    '#0D2547',
  navyAlt: '#1A2D4F',
  brass:   '#B8841C',
  cream:   '#FAFAF7',
  paper:   '#FFFFFF',
  ink:     '#0A0F1A',
  text:    '#1A1F2E',
  muted:   '#6B7280',
  border:  '#E5E5E0',
  warm:    '#F5F3EE',
  ok:      '#1F4733',
  okBg:    '#D9E8DC',
  warn:    '#B8761C',
  warnBg:  '#F5E6CC',
  bad:     '#8B2A1E',
  badBg:   '#F5DDD9',
  info:    '#1A5490',
  infoBg:  '#D9E5F2',
};

export const serif = 'Fraunces, Georgia, serif';
export const sans  = 'Geist, system-ui, -apple-system, sans-serif';

export const fmt = {
  money: (n, moneda = 'PEN') =>
    new Intl.NumberFormat('es-PE', { style: 'currency', currency: moneda })
      .format(Number(n) || 0),
  // Una fecha suelta como "2026-09-01" la interpreta el navegador como
  // medianoche UTC. En Lima eso es el 31 de agosto a las 7 pm, así que
  // toda fecha se mostraba un día antes. Se arma a mano para que el
  // día escrito sea el día mostrado.
  fecha: (d) => {
    if (!d) return '—';
    const t = String(d);
    const soloFecha = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
    const fecha = soloFecha
      ? new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
      : new Date(t);
    if (Number.isNaN(fecha.getTime())) return '—';
    return fecha.toLocaleDateString('es-PE',
      { day: '2-digit', month: 'short', year: 'numeric' });
  },

  // Una marca de tiempo completa sí lleva zona horaria: esa se
  // convierte como siempre.
  fechaHora: (d) => (d ? new Date(d).toLocaleString('es-PE',
    { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'),
};

export const ESTADOS = {
  anulado:  { texto: 'Anulado',  color: '#9B2C1E', fondo: '#F8E5E1' },
  anulada:  { texto: 'Anulada',  color: '#9B2C1E', fondo: '#F8E5E1' },
  borrador:      { texto: 'Borrador',      color: T.muted, fondo: T.warm },
  enviado:       { texto: 'Por aprobar',   color: T.info,  fondo: T.infoBg },
  observado:     { texto: 'Observado',     color: T.warn,  fondo: T.warnBg },
  aprobado:      { texto: 'Aprobado',      color: T.ok,    fondo: T.okBg },
  rechazado:     { texto: 'Rechazado',     color: T.bad,   fondo: T.badBg },
  contabilizado: { texto: 'En SAP',        color: T.navy,  fondo: T.infoBg },
  pendiente:     { texto: 'En cola',       color: T.info,  fondo: T.infoBg },
  tomado:        { texto: 'Enviando',      color: T.warn,  fondo: T.warnBg },
  ok:            { texto: 'En SAP',        color: T.ok,    fondo: T.okBg },
  error:         { texto: 'Error',         color: T.bad,   fondo: T.badBg },
};

export const ROL_TEXTO = {
  operativo: 'Operativo',
  mando_medio: 'Mando medio',
  contabilidad: 'Contabilidad',
  director: 'Dirección',
};

// ── Primitivas ──────────────────────────────────────────────────────

export function Chip({ estado, children }) {
  const e = ESTADOS[estado] || { texto: estado, color: T.muted, fondo: T.warm };
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 12,
      fontSize: 11, fontWeight: 600, letterSpacing: '.02em',
      color: e.color, background: e.fondo, whiteSpace: 'nowrap',
    }}>
      {children || e.texto}
    </span>
  );
}

export function Card({ children, style, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: T.paper, border: `1px solid ${T.border}`, borderRadius: 10,
        padding: 18, cursor: onClick ? 'pointer' : 'default', ...style,
      }}
    >
      {children}
    </div>
  );
}

export function Boton({ children, variante = 'primario', ...props }) {
  const variantes = {
    primario:   { background: T.navy,  color: '#fff', border: 'none' },
    acento:     { background: T.brass, color: T.navy, border: 'none' },
    suave:      { background: T.paper, color: T.text, border: `1px solid ${T.border}` },
    peligro:    { background: T.bad,   color: '#fff', border: 'none' },
    exito:      { background: T.ok,    color: '#fff', border: 'none' },
  };
  // Igual que en Aviso: una variante que no exista pinta el botón
  // por defecto en lugar de tumbar la pantalla.
  const estilos = variantes[variante] || variantes.primario;

  return (
    <button
      {...props}
      style={{
        ...estilos, padding: '9px 16px', borderRadius: 8, fontSize: 14,
        fontWeight: 600, fontFamily: sans, cursor: 'pointer',
        opacity: props.disabled ? 0.5 : 1, ...props.style,
      }}
    >
      {children}
    </button>
  );
}

export function Campo({ etiqueta, ayuda, children }) {
  return (
    <label style={{ display: 'block', marginBottom: 14 }}>
      <span style={{
        display: 'block', fontSize: 12, fontWeight: 600, color: T.muted,
        marginBottom: 5, letterSpacing: '.03em', textTransform: 'uppercase',
      }}>
        {etiqueta}
      </span>
      {children}
      {ayuda && (
        <span style={{ display: 'block', fontSize: 11, color: T.muted, marginTop: 4 }}>
          {ayuda}
        </span>
      )}
    </label>
  );
}

export const estiloInput = {
  width: '100%', padding: '10px 12px', fontSize: 15, fontFamily: sans,
  border: `1px solid ${T.border}`, borderRadius: 8, background: T.paper,
  color: T.text, boxSizing: 'border-box',
};

export function Aviso({ tipo = 'info', children }) {
  const tipos = {
    info: { color: T.info, fondo: T.infoBg },
    ok:   { color: T.ok,   fondo: T.okBg },
    warn: { color: T.warn, fondo: T.warnBg },
    bad:  { color: T.bad,  fondo: T.badBg },
    // Sinónimos: se escribieron así en varias pantallas y con un tipo
    // desconocido el aviso reventaba la aplicación entera.
    alerta: { color: T.warn, fondo: T.warnBg },
    malo:   { color: T.bad,  fondo: T.badBg },
    error:  { color: T.bad,  fondo: T.badBg },
    exito:  { color: T.ok,   fondo: T.okBg },
  };

  // Un tipo que no exista cae en "info" en lugar de dejar la pantalla
  // en blanco. Un aviso mal pintado es un detalle; una aplicación
  // caída deja a alguien tirado en obra.
  const c = tipos[tipo] || tipos.info;
  return (
    <div style={{
      background: c.fondo, borderLeft: `3px solid ${c.color}`, color: c.color,
      padding: '12px 16px', borderRadius: '0 8px 8px 0', fontSize: 13,
      marginBottom: 16, lineHeight: 1.5,
    }}>
      {children}
    </div>
  );
}

export function Titulo({ children, sub }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <h1 style={{
        fontFamily: serif, fontSize: 30, fontWeight: 600, color: T.navy,
        margin: 0, letterSpacing: '-.02em',
      }}>
        {children}
      </h1>
      {sub && (
        <p style={{ color: T.muted, fontSize: 14, margin: '6px 0 0' }}>{sub}</p>
      )}
    </div>
  );
}

export function Vacio({ children }) {
  return (
    <div style={{
      textAlign: 'center', padding: '48px 20px', color: T.muted,
      fontSize: 14, background: T.warm, borderRadius: 10,
    }}>
      {children}
    </div>
  );
}

export function Cargando() {
  return <div style={{ padding: 40, textAlign: 'center', color: T.muted }}>Cargando…</div>;
}
